<template>
    <vm-container>
        <clere-title>
            File List
        </clere-title>
        <div class="mb-3">
            <vm-button 
                primaryColor="var(--triadic2-600)" 
                @click="handleUploadFile()">
                UPLOAD FILE
            </vm-button>
            <input
                ref="fileInput"
                type="file"
                multiple
                style="display: none"
                @change="onFileSelected"
            />
        </div>
        <div class="mb-2 text-sm text-gray-600">
            <strong>Current Directory:</strong> {{ subpath.length ? subpath : "/" }}
        </div>

        <!-- Show go back button if not in root -->
        <div v-if="subpath.length" class="mb-3">
            <vm-button 
                primaryColor="var(--triadic2-600)" 
                @click="handleGoBack"
            >
                ← Parent Directory
            </vm-button>
        </div>
        <vm-card>
            <vm-grid
                :config="gridConfig"
                :rowData="fileList"
                :isLoading="isLoading || isUploading"
            >
            </vm-grid>
        </vm-card>
    </vm-container>

    <vm-modal ref="confirmDeleteModal">
        <vm-card-header>Delete File</vm-card-header>

        <vm-card-content>
            <confirm
                :closeModal="hideDeleteModal"
                :confirm="handleDeleteFile"
                primaryColor="var(--red-100)"
            >
                Are you sure you want to delete file {{ selectedFile.fileRelativePath }}?
            </confirm>
        </vm-card-content>
    </vm-modal>
</template>

<script lang="ts">
// vue
import { defineComponent, ref, onMounted } from 'vue';

// vue3-material
import { VmModal } from 'vue3-material';

// configs
import { tileManagerFileListConfig } from '@/config/tile-manager-file-list-config';

// components
import ClereTitle from '@/components/clere-title.vue';
import Confirm from '@/components/confirm.vue';
import { CrActionsConditional } from '@/components/grid-plugins';

// services
import { useTileManagementService } from '@/api/service/tile-management.service';

export default defineComponent({
    components: {
        ClereTitle,
        Confirm,
    },
    setup() {
        // services
        const {
            getFileList,
            downloadFile,
            uploadFile,
            deleteFile
        } = useTileManagementService();

        const confirmDeleteModal = ref<InstanceType<typeof VmModal>>();

        // properties
        const selectedFile = ref<any>({filename: '', field: '', fileRelativePath: ''});
        const isEditing = ref(false);
        const isLoading = ref(true);

        const isUploading = ref(false);

        const fileList = ref<any>([]);

        const subpath = ref<string>("");

        const fileInput = ref<HTMLInputElement>();

        const hideDeleteModal = () => {
            confirmDeleteModal.value.hide();
        };

        const showDeleteModal = async (file: any) => {
            if (file && file.filename) {
                selectedFile.value = file;
                confirmDeleteModal.value.show(); 
            }
        };

        const handleGetFileList = async () => {
            const {files} = await getFileList(subpath.value);
            const filteredFiles = (files||[]).filter((x:any) => x.filePath !== '.')
                                        .toSorted((a:any, b:any) => (a.filename > b.filename) ? 1 : (b.filename > a.filename) ? -1 : 0)
                                        .toSorted((a:any, b:any) => (a.type > b.type) ? 1 : (b.type > a.type) ? -1 : 0);
            fileList.value = [...filteredFiles];
        }

        const onFileSelected = async (event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files?.length) return;

            isUploading.value = true;

            try {
                    const files = Array.from(target.files);
                    await uploadFile(files, subpath.value);
                    await handleGetFileList();
                } catch (e) {
                    // eslint-disable-next-line no-use-before-define
                    console.error(e); // eslint-disable-line
                } finally {
                    isUploading.value = false;
                    target.value = '';
                }

        };


        const handleUploadFile = async () => {
            // eslint-disable-next-line no-use-before-define
            fileInput.value?.click(); // eslint-disable-line
        }

        const handleDownloadFile = async (file:any) => {
            const response = await downloadFile(file.filePath);
            const blob = new Blob([response.data]);

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = file.filename;
            link.click();

            link.remove();

        }

        const handleDeleteFile = async () => {
            if (selectedFile.value?.type === 'directory' || !selectedFile.value?.filePath) return;

            await deleteFile(selectedFile.value.filePath);
            hideDeleteModal();
            await handleGetFileList();
        }

        const handleOpenFolder = async (file:any) => {
            if (file.type !== 'directory') return;
            subpath.value = file.filePath;
            await handleGetFileList();
        }

        const handleGoBack = async () => {
            const x = subpath.value.split("/");
            if (x.length < 2) {
                subpath.value = "";
            }
            else {
                x.pop();
                subpath.value = x.join("/")
            }
            await handleGetFileList();
        }

        // lifecycle hooks
        onMounted(async () => {
            isLoading.value = true;
            await handleGetFileList();
            isLoading.value = false;
        });

        // configs
        const gridConfig = [
            ...tileManagerFileListConfig,
            {
                title: 'Actions',
                fitWidth: true,
                cellRenderer: {
                    component: CrActionsConditional,
                    exported: {
                        actions: [
                            {
                                icon: 'fal fa-download',
                                callback: handleDownloadFile,
                                condition: {field: 'type', value: 'file'}
                            },
                            {
                                icon: 'fal fa-folder-open',
                                callback: handleOpenFolder,
                                condition: {field: 'type', value: 'directory'}
                            },
                            {
                                icon: 'fal fa-trash-alt',
                                callback: showDeleteModal,
                                condition: {field: 'type', value: 'file'}
                            },
                        ],
                    },
                },
            },
        ];

        return {
            isEditing,
            selectedFile,
            subpath,
            handleUploadFile,
            handleDownloadFile,
            handleDeleteFile,
            handleOpenFolder,
            handleGoBack,
            fileList,
            fileInput,
            onFileSelected,
            gridConfig,
            showDeleteModal,
            confirmDeleteModal,
            hideDeleteModal,
            isLoading,
            isUploading,
        };
    },
});
</script>

<style scoped></style>
